// src/components/landingpage/LandingPageNav.jsx
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  Stack
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

const EXTERNAL_APP_URL =
  'https://digitalmortgage.candidcrm.com/Jet-HomeLoans/005fI000000hPWD&00DfI000004W7Ue';

function LandingPageNav({ hideApply = false }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [resourcesAnchorEl, setResourcesAnchorEl] = useState(null);
  const [loanProgramsAnchorEl, setLoanProgramsAnchorEl] = useState(null);
  const [alternativeOptionsAnchorEl, setAlternativeOptionsAnchorEl] = useState(null);
  const [mobileLoanProgramsOpen, setMobileLoanProgramsOpen] = useState(false);
  const [mobileAlternativeOptionsOpen, setMobileAlternativeOptionsOpen] = useState(false);

  const [openAsk, setOpenAsk] = useState(false);
  const [openNoFlow, setOpenNoFlow] = useState(false);

  // Nested loan-programs menu structure.
  // Items with `children` render as a sub-popout (Alternative Options).
  const loanProgramItems = [
    { label: 'All Programs', url: '/loan-programs' },
    { label: 'Conventional Financing', url: '/conventional' },
    { label: 'VA Loans', url: '/va' },
    { label: 'FHA Loans', url: '/fha' },
    { label: 'USDA Loans', url: '/usda' },
    { label: 'Refinancing', url: '/refinancing' },
    { label: 'Jumbo Loans', url: '/jumbo' },
    {
      label: 'Alternative Options',
      children: [
        { label: 'First Time Homebuyers', url: '/first-time-homebuyers' },
        { label: 'Low Down Payment', url: '/low-down-payment' },
        { label: 'DSCR', url: '/dscr' },
      ],
    },
  ];

  // Helper: close all nav popovers (used after navigation).
  const closeAllMenus = () => {
    setAnchorEl(null);
    setResourcesAnchorEl(null);
    setLoanProgramsAnchorEl(null);
    setAlternativeOptionsAnchorEl(null);
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = (url) => {
    setAnchorEl(null);
    if (url) navigate(url);
  };

  // ⭐ NEW: Careers added before Contact Us
  const menuItems = [
    { label: 'Find A Loan Officer', url: '/find-a-loan-officer' },
    { label: 'Submit A Payment', url: '/submit-payment' },
    { label: 'Careers', external: 'https://careers.dreamfindershomes.com/' },
    { label: 'Contact Us', url: '/contact' },
  ];

  const startApplyFlow = () => setOpenAsk(true);

  const handleAskYes = () => {
    setOpenAsk(false);
    navigate('/find-a-loan-officer');
  };

  const handleAskNo = () => {
    setOpenAsk(false);
    setOpenNoFlow(true);
  };

  const handleApplyExternal = () => {
    window.open(EXTERNAL_APP_URL, '_blank', 'noopener,noreferrer');
    setOpenNoFlow(false);
  };

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: '#1c1e2a', px: 2 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>

          {/* Logo */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <img
              src="/assets/logo-jet2.png"
              alt="Logo"
              style={{ height: 40, marginRight: 16 }}
            />
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

              <Button
                sx={{ color: 'white' }}
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setResourcesAnchorEl(e.currentTarget)}
              >
                Mortgage Resources
              </Button>
              <Menu
                anchorEl={resourcesAnchorEl}
                open={Boolean(resourcesAnchorEl)}
                onClose={() => setResourcesAnchorEl(null)}
              >
                <MenuItem onClick={() => { setResourcesAnchorEl(null); navigate('/calculators'); }}>Calculators</MenuItem>
                <MenuItem onClick={() => { setResourcesAnchorEl(null); navigate('/blog'); }}>Blog</MenuItem>
                <MenuItem
                  onClick={(e) => { setLoanProgramsAnchorEl(e.currentTarget); }}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  Loan Programs <ArrowRightIcon fontSize="small" />
                </MenuItem>
                <MenuItem onClick={() => { setResourcesAnchorEl(null); navigate('/purchase'); }}>Purchase</MenuItem>
                <MenuItem onClick={() => { setResourcesAnchorEl(null); navigate('/refinance'); }}>Refinance</MenuItem>
              </Menu>

              {/* Loan Programs Sub-Menu (level 2) */}
              <Menu
                anchorEl={loanProgramsAnchorEl}
                open={Boolean(loanProgramsAnchorEl)}
                onClose={() => setLoanProgramsAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {loanProgramItems.map((item) => {
                  if (item.children) {
                    // Render parent of a third-level popout (Alternative Options).
                    return (
                      <MenuItem
                        key={item.label}
                        onClick={(e) => setAlternativeOptionsAnchorEl(e.currentTarget)}
                        sx={{ display: 'flex', justifyContent: 'space-between' }}
                      >
                        {item.label} <ArrowRightIcon fontSize="small" />
                      </MenuItem>
                    );
                  }
                  return (
                    <MenuItem
                      key={item.url}
                      onClick={() => {
                        closeAllMenus();
                        navigate(item.url);
                      }}
                      sx={item.url === '/loan-programs' ? { fontWeight: 700 } : {}}
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
              </Menu>

              {/* Alternative Options Sub-Sub-Menu (level 3) */}
              <Menu
                anchorEl={alternativeOptionsAnchorEl}
                open={Boolean(alternativeOptionsAnchorEl)}
                onClose={() => setAlternativeOptionsAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {(loanProgramItems.find((i) => i.children)?.children ?? []).map((child) => (
                  <MenuItem
                    key={child.url}
                    onClick={() => {
                      closeAllMenus();
                      navigate(child.url);
                    }}
                  >
                    {child.label}
                  </MenuItem>
                ))}
              </Menu>

              {/* ⭐ UPDATED menu items (supports external link) */}
              {menuItems.map((item, index) =>
                item.external ? (
                  <Button
                    key={index}
                    sx={{ color: 'white' }}
                    onClick={() =>
                      window.open(item.external, '_blank', 'noopener,noreferrer')
                    }
                  >
                    {item.label}
                  </Button>
                ) : (
                  <Button
                    key={index}
                    sx={{ color: 'white' }}
                    onClick={() => navigate(item.url)}
                  >
                    {item.label}
                  </Button>
                )
              )}

              {!hideApply && (
  <Button
    variant="contained"
    sx={{
      backgroundColor: '#b89b5e',
      color: 'white',
      textTransform: 'uppercase',
      borderRadius: 1,
      px: 3,
      '&:hover': { backgroundColor: '#ab9767' },
    }}
    onClick={startApplyFlow}
  >
    Apply Now
  </Button>
)}

            </Box>
          )}

          {/* Mobile Menu */}
          {isMobile && (
            <>
              <IconButton edge="end" color="inherit" onClick={handleMenuOpen}>
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem onClick={() => handleMenuClose('/')}>Home</MenuItem>

                {/* Mortgage Resources sub-items (matches desktop) */}
                <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 700, fontSize: 14 }}>
                  Mortgage Resources
                </MenuItem>
                <MenuItem onClick={() => handleMenuClose('/calculators')} sx={{ pl: 4 }}>
                  Calculators
                </MenuItem>
                <MenuItem onClick={() => handleMenuClose('/blog')} sx={{ pl: 4 }}>
                  Blog
                </MenuItem>
                <MenuItem
                  onClick={() => setMobileLoanProgramsOpen(!mobileLoanProgramsOpen)}
                  sx={{ pl: 4, display: 'flex', justifyContent: 'space-between' }}
                >
                  Loan Programs <ArrowDropDownIcon fontSize="small" sx={{ transform: mobileLoanProgramsOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                </MenuItem>
                {mobileLoanProgramsOpen && loanProgramItems.map((item) => {
                  if (item.children) {
                    return (
                      <React.Fragment key={item.label}>
                        <MenuItem
                          onClick={() => setMobileAlternativeOptionsOpen(!mobileAlternativeOptionsOpen)}
                          sx={{ pl: 6, fontSize: 14, display: 'flex', justifyContent: 'space-between' }}
                        >
                          {item.label}
                          <ArrowDropDownIcon
                            fontSize="small"
                            sx={{
                              transform: mobileAlternativeOptionsOpen ? 'rotate(180deg)' : 'none',
                              transition: '0.2s',
                            }}
                          />
                        </MenuItem>
                        {mobileAlternativeOptionsOpen && item.children.map((child) => (
                          <MenuItem
                            key={child.url}
                            onClick={() => handleMenuClose(child.url)}
                            sx={{ pl: 8, fontSize: 13 }}
                          >
                            {child.label}
                          </MenuItem>
                        ))}
                      </React.Fragment>
                    );
                  }
                  return (
                    <MenuItem
                      key={item.url}
                      onClick={() => handleMenuClose(item.url)}
                      sx={{ pl: 6, fontSize: 14 }}
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
                <MenuItem onClick={() => handleMenuClose('/purchase')} sx={{ pl: 4 }}>
                  Purchase
                </MenuItem>
                <MenuItem onClick={() => handleMenuClose('/refinance')} sx={{ pl: 4 }}>
                  Refinance
                </MenuItem>

                {menuItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => {
                      setAnchorEl(null);
                      if (item.external) {
                        window.open(item.external, '_blank', 'noopener,noreferrer');
                      } else {
                        navigate(item.url);
                      }
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))}

                {!hideApply && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      startApplyFlow();
                    }}
                  >
                    Apply Now
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Modal 1 */}
      <Dialog
        open={openAsk}
        onClose={() => setOpenAsk(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, p: { xs: 2, md: 3 } } }}
      >
        <IconButton
          onClick={() => setOpenAsk(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={3} textAlign="center">
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Are you currently working with one of our Loan Officers?
            </Typography>
            <Typography color="text.secondary">
              This will save you time by connecting you with the right person.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                onClick={handleAskYes}
                sx={{ bgcolor: '#b79a59', '&:hover': { bgcolor: '#a88a4d' }, px: 5 }}
              >
                YES
              </Button>
              <Button
                variant="contained"
                onClick={handleAskNo}
                sx={{ bgcolor: '#b79a59', '&:hover': { bgcolor: '#a88a4d' }, px: 5 }}
              >
                NO
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Modal 2 */}
      <Dialog
        open={openNoFlow}
        onClose={() => setOpenNoFlow(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, p: { xs: 2, md: 3 } } }}
      >
        <IconButton
          onClick={() => setOpenNoFlow(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={3} textAlign="center">
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              No Problem! Get Started Today.
            </Typography>
            <Typography color="text.secondary">
              Start your application and we’ll connect you with the right person.
            </Typography>
            <Button
              variant="contained"
              onClick={handleApplyExternal}
              sx={{ bgcolor: '#b79a59', '&:hover': { bgcolor: '#a88a4d' }, px: 5 }}
            >
              CLICK HERE TO APPLY
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default LandingPageNav;
